export interface FileCacheFileInfo {
  fileName: string;
  fileSize: number;
  lastUsedAt: number;
  addedAt: number;
  /** Absolute on-disk path. Single source of truth — never reconstruct from fileName + cacheDirectory. */
  path: string;
}

export interface FileCacheFileOptions {
  /** The maximum number of files to return. */
  limit?: number;
  /** Whether to return the files in descending order of last used at. */
  descending?: boolean;
  /** The timestamp in milliseconds to start at (exclusive). */
  lastUsedAt?: number;
}

export interface FileCacheInfo {
  cacheSize: number;
  fileCount: number;
}

export interface FileCacheEvictResult {
  evicted: FileCacheFileInfo[];
  evictedBytes: number;
}

/**
 * Progress callback for a long-running eviction. Reports a fraction in (0, 1]
 * of the requested bytes freed so far, increasing monotonically and always
 * ending with exactly `1` once the eviction completes (including when the cache
 * empties before the target is reached, or when there was nothing to do).
 */
export type FileCacheProgressCallback = (progress: number) => void;

export const DEFAULT_FILE_CACHE_FILE_OPTIONS: FileCacheFileOptions = {
  limit: 100,
  descending: true,
  lastUsedAt: undefined,
};

/**
 * Contract for a file-backed LRU cache.
 *
 * Implementations move files onto disk under a managed directory, key them by
 * their bare file name, and evict least-recently-used entries to stay within
 * `maxCacheSize` bytes. There is no expiration: the only reasons an entry
 * leaves the cache are LRU eviction or an explicit removal.
 *
 * Callers must treat any returned path as best-effort — between obtaining the
 * path and reading the file, an LRU eviction or explicit removal can occur.
 */
export interface FileCacheInterface {
  // **************************************************************
  // Cache Settings
  // **************************************************************

  /** Synchronous read of the configured max cache size in bytes. */
  getMaxCacheSize(): number;

  /**
   * Synchronous read of the effective max cache size in bytes.
   *
   * The effective limit is `min(maxCacheSize, max(minCacheSize, freeSpace + cacheSize - freePaddingBytes))`,
   * where `freeSpace` is sampled once at {@link initialize} time. All eviction decisions
   * use this value rather than {@link getMaxCacheSize}.
   */
  getEffectiveMaxCacheSize(): number;

  /**
   * Update the max cache size. Recomputes the effective limit from the cached
   * free-space sample. If the new effective limit is below current usage,
   * evicts LRU entries until usage fits.
   */
  setMaxCacheSize(bytes: number): Promise<void>;

  // **************************************************************
  // Global cache operations
  // **************************************************************

  /**
   * Get the current cache info.
   */
  getCacheInfo(): Promise<FileCacheInfo>;

  /**
   * Free at least `bytes` of space via LRU. Whole files only — never partial.
   * No-op for `bytes ≤ 0` or an empty cache.
   *
   * @param bytes The minimum number of bytes to free.
   * @param onProgress Optional callback invoked as files are evicted with a
   *   0→1 fraction of the requested work completed. See {@link FileCacheProgressCallback}.
   * @returns The evicted entries and the number of bytes actually freed
   *   (≥ `bytes` unless the cache total is smaller).
   */
  evict(bytes: number, onProgress?: FileCacheProgressCallback): Promise<FileCacheEvictResult>;

  /**
   * Return the files in the cache.
   *
   * See: {@link DEFAULT_FILE_CACHE_FILE_OPTIONS} for the default options.
   *
   * @param options - See: {@link FileCacheFileOptions} for the options.
   * @returns The files in the cache.
   */
  getFiles(options?: FileCacheFileOptions): Promise<FileCacheFileInfo[]>;

  // **************************************************************
  // File operations
  // **************************************************************

  /**
   * Move the source file into the cache, keyed by its bare file name.
   * Returns the cached file's path (no `file://` prefix). Replaces any
   * existing entry with the same file name. Auto-evicts LRU entries as
   * needed. Throws if the single file exceeds `maxCacheSize`.
   */
  addFile(filePathOrUri: string): Promise<string>;

  /**
   * Ensure `fileName` is cached, fetching it from `url` only on a miss.
   *
   *   - hit:  freshen `lastUsedAt` and return the existing path (no fetch).
   *   - miss: download `url`, store it under `fileName`, evicting as needed to
   *           stay within `maxCacheSize`, and return the new path.
   *
   * The local cache key (`fileName`) is independent of the `url`. Returns the
   * cached file's path. Throws if the fetched file alone exceeds `maxCacheSize`.
   *
   * ASSUMES `fileName` uniquely identifies the desired content. Concurrent calls
   * for the same `fileName` are coalesced into one download (keyed by `fileName`
   * only), so a second caller passing a *different* `url` for the same
   * `fileName` piggybacks on the first — and a hit short-circuits before `url`
   * is even consulted. The cache does NOT derive `fileName` from `url`; choosing
   * a content-stable key is the caller's responsibility. For fetching arbitrary
   * URLs, hashing the `url` (or its content identity) into the `fileName` is the
   * recommended scheme.
   */
  getFetchedFile(fileName: string, url: string): Promise<string>;

  /**
   * Return the cached file's path, or `undefined` if it isn't present.
   * Updates `lastUsedAt` so future LRU eviction reflects this access.
   */
  getFile(fileName: string): Promise<string | undefined>;

  /**
   * Synchronous best-effort lookup. Returns the cached file's path iff the
   * file is currently *sync-available* to the implementation:
   *   - in-memory impls: every present file is sync-available
   *   - persistent-store impls (e.g. SQLite-backed): only entries currently
   *     warm in the in-memory hot mirror are sync-available; cold entries
   *     return `undefined` even when present on disk
   *
   * Does NOT update `lastUsedAt`. Designed for hot render-frame lookups where
   * blocking on async/DB access is unacceptable. Treat `undefined` as
   * "I don't know yet" and fall through to the async {@link getFile}.
   */
  getFileSync(fileName: string): string | undefined;

  /**
   * Return the cached file's size in bytes and last used at timestamp in milliseconds, or `undefined` if it isn't present.
   *
   * Pure read: does NOT update `lastUsedAt`. Use {@link getFile} when access should freshen LRU.
   */
  getFileInfo(fileName: string): Promise<FileCacheFileInfo | undefined>;

  /**
   * Bulk presence + metadata lookup. Returned map contains an entry only for
   * each input name that is currently in the cache; missing names are simply
   * absent from the map (i.e. `result.has(name)` is the presence test).
   *
   * Pure read: does NOT update `lastUsedAt`. Designed to answer "which of
   * these N files are cached and what are their sizes" in a single call —
   * e.g. resolving "best locally available size" for a photo's fallback
   * sequence without touching LRU order.
   */
  getFileInfos(fileNames: string[]): Promise<Map<string, FileCacheFileInfo>>;

  /** Remove a single file by name. Returns whether it was present. */
  evictFile(fileName: string): Promise<boolean>;

  /**
   * Bulk freshen + presence check.
   *
   * For every input key that is in the cache, update its `lastUsedAt` to now
   * (i.e. mark it as just-used for LRU purposes). The return value names
   * only the input keys that are *not* in the cache.
   *
   * Designed for prefetch/sync workflows that hand the cache a working set
   * of a few thousand keys and need to know which ones still need to be
   * fetched. Duplicate input keys are de-duplicated; the returned
   * `missingKeys` are unique.
   */
  freshen(fileNames: string[]): Promise<{ missingKeys: string[] }>;
}
